import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icons } from '@/shared/ui';

import { formatBytes, MAX_ATTACHMENT_BYTES } from './labels';
import type { useAttachmentDraft } from './use-attachment-draft';
import styles from './AttachmentPicker.module.css';

interface Props {
  draft: ReturnType<typeof useAttachmentDraft>;
  /** A short variant for the reply box. */
  compact?: boolean;
}

/**
 * File picker + drop zone + previews for a draft. Pasting a screenshot works anywhere in the form
 * that owns `draft.onPaste`; the hint says so.
 */
export function AttachmentPicker({ draft, compact = false }: Props) {
  const { t, i18n } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const hintId = useId();
  return (
    <div
      className={`${styles.picker} ${compact ? styles.compact : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDrop={draft.onDrop}
    >
      <div className={styles.bar}>
        <input
          ref={input}
          type="file"
          multiple
          hidden
          data-testid="feedback-file-input"
          onChange={(event) => {
            draft.add(Array.from(event.target.files ?? []));
            event.target.value = '';
          }}
        />
        <Button
          size="sm"
          disabled={draft.full}
          aria-describedby={hintId}
          onClick={() => {
            input.current?.click();
          }}
        >
          <Icons.paperclip /> {t('feedback.attach.button')}
        </Button>
        <span id={hintId} className={styles.hint}>
          {t('feedback.attach.hint', { size: formatBytes(MAX_ATTACHMENT_BYTES, i18n.language) })}
        </span>
      </div>
      {draft.files.length > 0 && (
        <ul className={styles.list} aria-label={t('feedback.attach.listLabel')}>
          {draft.files.map((file) => (
            <li
              key={file.key}
              className={styles.item}
              data-state={file.state}
              data-testid="feedback-draft-file"
            >
              {file.previewUrl ? (
                <img className={styles.thumb} src={file.previewUrl} alt="" />
              ) : (
                <span className={styles.fileIcon} aria-hidden>
                  <Icons.file />
                </span>
              )}
              <span className={styles.meta}>
                <span className={styles.name}>{file.name || t('feedback.attach.tooMany')}</span>
                <span
                  className={file.state === 'error' ? styles.error : styles.status}
                  role={file.state === 'error' ? 'alert' : undefined}
                >
                  {file.state === 'uploading' && t('feedback.attach.uploading')}
                  {file.state === 'ready' && formatBytes(file.size, i18n.language)}
                  {file.state === 'error' && t(`feedback.attach.error.${file.error ?? 'failed'}`)}
                </span>
              </span>
              <button
                type="button"
                className={styles.remove}
                aria-label={t('feedback.attach.remove', { name: file.name || '…' })}
                onClick={() => {
                  draft.remove(file.key);
                }}
              >
                <Icons.close />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
